import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Copy,
  ArrowUpRight,
  ChevronDown,
  ChevronUp,
  Clock,
  MapPin,
  Users,
  Video,
  Pencil,
  Trash,
  Loader2,
  RefreshCw,
  Play,
  FileText,
  Clipboard,
  ClipboardList,
  Stethoscope,
  HeartPulse,
  Pill,
  User,
  AlertCircle,
  Mic,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BookingResponse,
  BookingStatus,
  EventTypeCategory,
  OrganisationMemberResponse,
  PatientRead,
} from '@/client/types.gen';
import {
  cancelBooking,
  getOrganisationMembers,
  getPatient,
} from '@/client/sdk.gen';
import { useToast } from '@/hooks/use-toast';
import { Separator } from '@/components/ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useNavigate } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';

// Define interfaces for location details
interface LocationDetails {
  link?: string;
  [key: string]: unknown;
}

interface BookingLocation {
  type?: string;
  details?: LocationDetails;
}

// Extend the BookingResponse type to include uid
interface ExtendedBookingResponse extends BookingResponse {
  uid: string;
  event_type_category?: EventTypeCategory;
  location_details?: BookingLocation | string | null;
  notes?: string | null;
}

interface AppointmentListProps {
  bookings: ExtendedBookingResponse[];
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  isLoading?: boolean; // Added to indicate when bookings are loading initially
  refreshList?: () => Promise<void>; // Method to refresh the list of bookings
}

// Define appointment categories
type AppointmentCategory = 'ONGOING' | 'COMPLETED' | 'CANCELLED' | 'UPCOMING';

export default function AppointmentList({
  bookings,
  currentPage,
  totalPages,
  onPageChange,
  isLoading = false,
  refreshList,
}: AppointmentListProps) {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [cancelingId, setCancelingId] = useState<string | null>(null);
  const [reschedulingId, setReschedulingId] = useState<string | null>(null);
  const [expandedBookingId, setExpandedBookingId] = useState<string | null>(
    null
  );
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [organisationMembers, setOrganisationMembers] = useState<
    OrganisationMemberResponse[]
  >([]);
  const [startingAppointmentId, setStartingAppointmentId] = useState<
    string | null
  >(null);
  const [activeCategory, setActiveCategory] =
    useState<AppointmentCategory>('UPCOMING');
  const [patientData, setPatientData] = useState<Record<string, PatientRead>>(
    {}
  );
  const [fetchingPatientIds, setFetchingPatientIds] = useState<string[]>([]);

  // Calculate counts for each category
  const categoryCounts = {
    ONGOING: bookings.filter((b) => b.actual_start_time && !b.actual_end_time)
      .length,
    COMPLETED: bookings.filter((b) => b.actual_start_time && b.actual_end_time)
      .length,
    CANCELLED: bookings.filter((b) => b.status === 'CANCELLED').length,
    UPCOMING: bookings.filter(
      (b) =>
        !b.actual_start_time && !b.actual_end_time && b.status !== 'CANCELLED'
    ).length,
  };

  // Filter bookings based on active category
  const filteredBookings = bookings.filter((booking) => {
    switch (activeCategory) {
      case 'ONGOING':
        return booking.actual_start_time && !booking.actual_end_time;
      case 'COMPLETED':
        return booking.actual_start_time && booking.actual_end_time;
      case 'CANCELLED':
        return booking.status === 'CANCELLED';
      case 'UPCOMING':
        return (
          !booking.actual_start_time &&
          !booking.actual_end_time &&
          booking.status !== 'CANCELLED'
        );
      default:
        return true;
    }
  });

  useEffect(() => {
    // Fetch organisation members when component mounts
    const fetchOrganisationMembers = async () => {
      try {
        const response = await getOrganisationMembers();
        if (response.data) {
          setOrganisationMembers(response.data.members);
        }
      } catch (error) {
        console.error('Failed to fetch organisation members:', error);
      }
    };

    fetchOrganisationMembers();
  }, []);

  // Fetch patient data when expandedBookingId changes
  useEffect(() => {
    if (!expandedBookingId) return;

    const expandedBooking = bookings.find((b) => b.uid === expandedBookingId);
    if (!expandedBooking || !expandedBooking.patient_id) return;

    // Check if we already have the data or are fetching it
    if (
      patientData[expandedBooking.patient_id] ||
      fetchingPatientIds.includes(expandedBooking.patient_id)
    )
      return;

    const fetchPatientData = async (patientId: string) => {
      setFetchingPatientIds((prev) => [...prev, patientId]);

      try {
        const response = await getPatient({
          path: { patient_id: patientId },
        });

        if (response.data) {
          setPatientData((prev) => ({
            ...prev,
            [patientId]: response.data,
          }));
        }
      } catch (error) {
        console.error(
          `Failed to fetch patient data for ID ${patientId}:`,
          error
        );
      } finally {
        setFetchingPatientIds((prev) => prev.filter((id) => id !== patientId));
      }
    };

    fetchPatientData(expandedBooking.patient_id);
  }, [expandedBookingId, bookings, patientData, fetchingPatientIds]);

  const statusColorMap: Record<BookingStatus, string> = {
    CONFIRMED:
      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
    PENDING:
      'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
    CANCELLED:
      'bg-gray-100 text-gray-800 dark:bg-gray-800/50 dark:text-gray-300',
    RESCHEDULED:
      'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
    REJECTED: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
    ACCEPTED:
      'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
  };

  // Add this helper function after the statusColorMap definition
  const getAttendeeStats = (invitees: BookingResponse['invitees']) => {
    const total = invitees.length;
    const confirmed = invitees.filter(
      (inv) => inv.response_status === 'ACCEPTED'
    ).length;
    return { total, confirmed };
  };

  // Helper function to determine if we can cancel a booking
  const canCancel = (status: BookingStatus): boolean => {
    return ['CONFIRMED', 'PENDING', 'ACCEPTED'].includes(status);
  };

  // Helper function to determine if we can reschedule a booking
  const canReschedule = (status: BookingStatus): boolean => {
    return ['CONFIRMED', 'PENDING', 'ACCEPTED'].includes(status);
  };

  // Helper function to determine if we can join a meeting
  const canJoin = (status: BookingStatus): boolean => {
    return ['CONFIRMED', 'ACCEPTED'].includes(status);
  };

  // Helper function to check if a user is an organisation member
  const isOrganisationMember = (email: string): boolean => {
    return organisationMembers.some((member) => member.email === email);
  };

  // Helper function to determine if we can start an appointment
  const canStartAppointment = (status: BookingStatus): boolean => {
    // Can start appointments that are confirmed, accepted, or pending
    if (!['CONFIRMED', 'ACCEPTED', 'PENDING'].includes(status)) {
      return false;
    }

    // Remove time restriction completely
    return true;
  };

  // Function to refresh the booking list
  const handleRefresh = async () => {
    if (isRefreshing || !refreshList) return;

    setIsRefreshing(true);
    try {
      await refreshList();
      toast({
        title: 'Refreshed',
        description: 'Patient visits list has been refreshed.',
      });
    } catch {
      toast({
        title: 'Error',
        description: 'Failed to refresh patient visits list. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleCancelBooking = async (
    bookingId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setCancelingId(bookingId);
    try {
      await cancelBooking({
        path: {
          booking_id: bookingId,
        },
      });

      toast({
        title: 'Visit cancelled',
        description: 'The patient visit has been successfully cancelled.',
      });

      // Refresh the list after cancellation
      if (refreshList) {
        await refreshList();
      }
    } catch (error) {
      console.error('Error cancelling patient visit:', error);
      toast({
        title: 'Error',
        description: 'Failed to cancel the patient visit. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCancelingId(null);
    }
  };

  const handleRescheduleBooking = async (
    bookingId: string,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    setReschedulingId(bookingId);
    try {
      // Navigate to reschedule page
      navigate(`/reschedule/${bookingId}`);
    } catch (error) {
      console.error('Error navigating to reschedule page:', error);
      toast({
        title: 'Error',
        description: 'Failed to reschedule the appointment. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setReschedulingId(null);
    }
  };

  const handleStartAppointment = (bookingId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setStartingAppointmentId(bookingId);

    // Navigate to the appointment page with the booking ID
    navigate(`/dashboard/appointments/encounter?booking_id=${bookingId}`);
  };

  const toggleExpand = (bookingId: string) => {
    setExpandedBookingId((prev) => (prev === bookingId ? null : bookingId));
  };

  const copyBookingLink = (bookingUid: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const link = `${window.location.origin}/booking/${bookingUid}`;
    navigator.clipboard.writeText(link);
    toast({
      title: 'Link copied',
      description: 'Appointment link copied to clipboard',
    });
  };

  const getHeaderForBooking = (booking: ExtendedBookingResponse) => {
    // Get the patient name from the first invitee
    const patientName =
      booking.invitees && booking.invitees.length > 0
        ? booking.invitees[0].invitee_info.name
        : 'Unnamed patient';

    // Get appointment type from the title
    const appointmentType = booking.title || 'General consultation';

    return (
      <div className="flex flex-col md:flex-row justify-between md:items-center gap-2">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-primary" />
            <span className="font-medium">{patientName}</span>
            <Badge
              variant="secondary"
              className={
                statusColorMap[booking.status as BookingStatus] ||
                'bg-gray-100 text-gray-800'
              }
            >
              {booking.status}
            </Badge>
          </div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Stethoscope className="h-3 w-3" />
            <span>{appointmentType}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            <Calendar className="h-4 w-4" />
            <span>
              {format(new Date(booking.start_time), 'EEEE, MMMM d, yyyy')}
            </span>
          </div>
          <div className="flex items-center gap-1 text-sm text-muted-foreground ml-2">
            <Clock className="h-4 w-4" />
            <span>
              {format(new Date(booking.start_time), 'h:mm a')} -{' '}
              {format(new Date(booking.end_time), 'h:mm a')}
            </span>
          </div>

          {/* Add Start Visit button to header for easier access */}
          {canStartAppointment(booking.status as BookingStatus) && (
            <Button
              variant="default"
              size="sm"
              className="ml-2 flex items-center gap-1"
              onClick={(e) => {
                e.stopPropagation();
                handleStartAppointment(booking.id, e);
              }}
              disabled={startingAppointmentId === booking.id}
            >
              {startingAppointmentId === booking.id ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Play className="h-3 w-3" />
              )}
              Start Visit
            </Button>
          )}
        </div>
      </div>
    );
  };

  const renderAttendees = (booking: ExtendedBookingResponse) => {
    if (!booking.invitees || booking.invitees.length === 0) {
      return (
        <p className="text-sm text-muted-foreground">
          No patient information found.
        </p>
      );
    }

    // Get stats for group events
    let attendeeStats;
    if (
      booking.event_type_category === 'GROUP' &&
      booking.invitees.length > 0
    ) {
      attendeeStats = getAttendeeStats(booking.invitees);
    }

    // Get the patient (first invitee)
    const patient = booking.invitees[0];

    // Check if we have patient data from our records
    const patientInfo = booking.patient_id
      ? patientData[booking.patient_id]
      : null;
    const isLoadingPatientData =
      booking.patient_id && fetchingPatientIds.includes(booking.patient_id);

    return (
      <div className="space-y-2">
        {/* For group events, show stats */}
        {booking.event_type_category === 'GROUP' && attendeeStats && (
          <div className="bg-muted/50 p-2 rounded-md mb-2 flex gap-2 items-center">
            <Users className="h-4 w-4 text-muted-foreground" />
            <span className="text-sm">
              {`${attendeeStats.confirmed} of ${attendeeStats.total} confirmed`}
            </span>
          </div>
        )}

        {/* Show patient information */}
        <div className="space-y-3">
          <div className="flex flex-col gap-1 p-3 bg-muted/30 rounded-md">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-sm font-medium">
                    {patientInfo?.name
                      ? patientInfo.name.charAt(0).toUpperCase()
                      : patient.invitee_info.name
                      ? patient.invitee_info.name.charAt(0).toUpperCase()
                      : patient.invitee_info.email.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="flex flex-col">
                  <span className="font-medium">
                    {patientInfo?.name ||
                      patient.invitee_info.name ||
                      'Unnamed patient'}
                    {isOrganisationMember(patient.invitee_info.email) && (
                      <Badge
                        variant="outline"
                        className="ml-2 px-1 py-0 h-4 text-[10px]"
                      >
                        Staff
                      </Badge>
                    )}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {patientInfo?.email || patient.invitee_info.email}
                  </span>
                </div>
              </div>
              <Badge
                variant="secondary"
                className={
                  patient.response_status === 'ACCEPTED'
                    ? 'bg-green-100 text-green-800'
                    : patient.response_status === 'DECLINED'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-yellow-100 text-yellow-800'
                }
              >
                {patient.response_status || 'PENDING'}
              </Badge>
            </div>

            {isLoadingPatientData ? (
              <div className="flex items-center justify-center py-2">
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                <span className="text-xs">Loading patient data...</span>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
                {/* Medical Record Number */}
                <div className="flex items-center gap-1">
                  <Clipboard className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {patientInfo
                      ? `MRN: ${patientInfo.id.slice(0, 8)}`
                      : 'No MRN available'}
                  </span>
                </div>

                {/* Last Visit */}
                <div className="flex items-center gap-1">
                  <HeartPulse className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {patientInfo?.visits && patientInfo.visits.length > 0
                      ? `Last visit: ${format(
                          new Date(patientInfo.visits[0].visit_date),
                          'MM/dd/yyyy'
                        )}`
                      : 'No previous visits'}
                  </span>
                </div>

                {/* Allergies */}
                <div className="flex items-center gap-1">
                  <AlertCircle className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {patientInfo?.allergies && patientInfo.allergies.length > 0
                      ? `Allergies: ${patientInfo.allergies
                          .map((a) => a.allergen)
                          .join(', ')}`
                      : 'No known allergies'}
                  </span>
                </div>

                {/* Medications */}
                <div className="flex items-center gap-1">
                  <Pill className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className="text-xs text-muted-foreground">
                    {patientInfo?.medications &&
                    patientInfo.medications.length > 0
                      ? `Medications: ${
                          patientInfo.medications.filter((m) => m.start_date)
                            .length
                        } active`
                      : 'No medications'}
                  </span>
                </div>

                {/* Date of Birth */}
                {patientInfo?.date_of_birth && (
                  <div className="flex items-center gap-1">
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      DOB:{' '}
                      {format(
                        new Date(patientInfo.date_of_birth),
                        'MM/dd/yyyy'
                      )}
                    </span>
                  </div>
                )}

                {/* Gender */}
                {patientInfo?.gender && (
                  <div className="flex items-center gap-1">
                    <User className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-xs text-muted-foreground">
                      Gender:{' '}
                      {patientInfo.gender.charAt(0).toUpperCase() +
                        patientInfo.gender.slice(1)}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* List other attendees if this is a group event */}
          {booking.invitees.length > 1 && (
            <div className="mt-2">
              <h4 className="text-xs font-medium mb-1">Other attendees:</h4>
              {booking.invitees.slice(1).map((invitee, i) => (
                <div
                  key={i}
                  className="flex items-center justify-between gap-2 p-2 bg-muted/20 rounded-md mb-1"
                >
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-full bg-primary/10 flex items-center justify-center">
                      <span className="text-xs">
                        {invitee.invitee_info.name
                          ? invitee.invitee_info.name.charAt(0).toUpperCase()
                          : invitee.invitee_info.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <span className="text-xs">
                      {invitee.invitee_info.name || invitee.invitee_info.email}
                    </span>
                  </div>
                  <Badge
                    variant="secondary"
                    className="text-[10px] px-1 py-0 h-4"
                  >
                    {invitee.response_status || 'PENDING'}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderClinicalInfo = (booking: ExtendedBookingResponse) => {
    // Get patient data if available
    const patientInfo = booking.patient_id
      ? patientData[booking.patient_id]
      : null;
    const isLoadingPatientData =
      booking.patient_id && fetchingPatientIds.includes(booking.patient_id);

    if (isLoadingPatientData) {
      return (
        <div className="p-3 bg-muted/30 rounded-md space-y-2">
          <div className="flex items-center justify-center py-2">
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
            <span className="text-sm">Loading clinical information...</span>
          </div>
        </div>
      );
    }

    return (
      <div className="p-3 bg-muted/30 rounded-md space-y-2">
        <div>
          <h4 className="text-xs font-medium">Chief Complaint</h4>
          <p className="text-sm">
            {booking.notes || 'No chief complaint recorded'}
          </p>
        </div>

        <div>
          <h4 className="text-xs font-medium">Visit Type</h4>
          <p className="text-sm">{booking.title || 'General consultation'}</p>
        </div>

        {patientInfo?.vitals && patientInfo.vitals.length > 0 ? (
          <div>
            <h4 className="text-xs font-medium">
              Last Vitals (
              {format(
                new Date(patientInfo.vitals[0].recorded_at),
                'MM/dd/yyyy'
              )}
              )
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
              <div className="bg-muted/50 p-1 rounded text-xs">
                BP: {patientInfo.vitals[0].blood_pressure_systolic || '--'}/
                {patientInfo.vitals[0].blood_pressure_diastolic || '--'}
              </div>
              <div className="bg-muted/50 p-1 rounded text-xs">
                HR: {patientInfo.vitals[0].heart_rate || '--'} bpm
              </div>
              <div className="bg-muted/50 p-1 rounded text-xs">
                Temp: {patientInfo.vitals[0].temperature || '--'}°
                {patientInfo.vitals[0].temperature_unit || 'F'}
              </div>
              <div className="bg-muted/50 p-1 rounded text-xs">
                SpO2: {patientInfo.vitals[0].oxygen_saturation || '--'}%
              </div>
            </div>
          </div>
        ) : (
          <div>
            <h4 className="text-xs font-medium">Last Vitals</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
              <div className="bg-muted/50 p-1 rounded text-xs">BP: --/--</div>
              <div className="bg-muted/50 p-1 rounded text-xs">HR: -- bpm</div>
              <div className="bg-muted/50 p-1 rounded text-xs">Temp: --°F</div>
              <div className="bg-muted/50 p-1 rounded text-xs">SpO2: --%</div>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderLocationDetails = (booking: ExtendedBookingResponse) => {
    if (!booking.location_details) {
      return <p className="text-sm text-muted-foreground">No location set.</p>;
    }

    try {
      // Try to parse location_details if it's a string
      let locationDetails: BookingLocation = {};
      if (typeof booking.location_details === 'string') {
        try {
          locationDetails = JSON.parse(booking.location_details);
        } catch {
          // If parsing fails, use as-is
          locationDetails = {
            type: 'other',
            details: { value: booking.location_details },
          };
        }
      } else {
        // Already an object
        locationDetails = booking.location_details as BookingLocation;
      }

      // Get content based on location type
      let locationContent;
      switch (locationDetails.type) {
        case 'google_meet':
          locationContent = (
            <>
              <div className="flex items-center gap-2">
                <Video className="h-4 w-4 text-muted-foreground" />
                <span>Google Meet</span>
              </div>
              {locationDetails.details?.link && (
                <a
                  href={locationDetails.details.link as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline flex items-center gap-1 mt-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  Join meeting <ArrowUpRight className="h-3 w-3" />
                </a>
              )}
            </>
          );
          break;
        case 'zoom':
          locationContent = (
            <>
              <div className="flex items-center gap-2">
                <Video className="h-4 w-4 text-muted-foreground" />
                <span>Zoom</span>
              </div>
              {locationDetails.details?.link && (
                <a
                  href={locationDetails.details.link as string}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-primary hover:underline flex items-center gap-1 mt-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  Join meeting <ArrowUpRight className="h-3 w-3" />
                </a>
              )}
            </>
          );
          break;
        case 'in_person':
          locationContent = (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>
                {locationDetails.details && 'address' in locationDetails.details
                  ? (locationDetails.details.address as string)
                  : 'Address not provided (In-person)'}
              </span>
            </div>
          );
          break;
        default:
          locationContent = (
            <div className="flex items-center gap-2">
              <MapPin className="h-4 w-4 text-muted-foreground" />
              <span>
                {typeof booking.location_details === 'string'
                  ? booking.location_details
                  : 'Location details available'}
              </span>
            </div>
          );
      }

      return <div className="space-y-1">{locationContent}</div>;
    } catch (error) {
      console.error('Error rendering location details:', error);
      return (
        <p className="text-sm text-muted-foreground">
          {typeof booking.location_details === 'string'
            ? booking.location_details
            : 'Location details available but could not be parsed.'}
        </p>
      );
    }
  };

  // Create pagination buttons/controls
  const renderPagination = () => {
    const pages = [];
    const maxDisplayedPages = 5; // Maximum number of page buttons to show

    let startPage = Math.max(
      1,
      currentPage - Math.floor(maxDisplayedPages / 2)
    );
    const endPage = Math.min(totalPages, startPage + maxDisplayedPages - 1);

    // Adjust start page if end page is maxed out
    if (endPage === totalPages && endPage > maxDisplayedPages) {
      startPage = Math.max(1, endPage - maxDisplayedPages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Button
          key={i}
          variant={i === currentPage ? 'default' : 'outline'}
          size="icon"
          onClick={() => onPageChange(i)}
          className="w-9 h-9"
        >
          {i}
        </Button>
      );
    }

    return (
      <div className="flex items-center justify-center gap-1 mt-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="w-9 h-9"
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>

        {startPage > 1 && (
          <>
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange(1)}
              className="w-9 h-9"
            >
              1
            </Button>
            {startPage > 2 && (
              <span className="mx-1 text-muted-foreground">...</span>
            )}
          </>
        )}

        {pages}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && (
              <span className="mx-1 text-muted-foreground">...</span>
            )}
            <Button
              variant="outline"
              size="icon"
              onClick={() => onPageChange(totalPages)}
              className="w-9 h-9"
            >
              {totalPages}
            </Button>
          </>
        )}

        <Button
          variant="outline"
          size="icon"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="w-9 h-9"
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    );
  };

  // Display a count badge if count > 0
  const renderCount = (count: number) => {
    if (count <= 0) return null;
    return (
      <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-xs">
        {count > 99 ? '99+' : count}
      </span>
    );
  };

  // Modify the main rendering of expanded content
  const renderExpandedContent = (booking: ExtendedBookingResponse) => {
    return (
      <div className="mt-4 space-y-4">
        <Separator className="my-2" />

        {/* Patient Information */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <User className="h-4 w-4" />
            Patient Information
          </h3>
          {renderAttendees(booking)}
        </div>

        {/* Clinical Information */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <ClipboardList className="h-4 w-4" />
            Clinical Information
          </h3>
          {renderClinicalInfo(booking)}
        </div>

        {/* Location */}
        <div className="space-y-2">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            <MapPin className="h-4 w-4" />
            Visit Location
          </h3>
          {renderLocationDetails(booking)}
        </div>

        {/* Actions */}
        <div className="pt-2 flex flex-wrap gap-2">
          {canReschedule(booking.status as BookingStatus) && (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              onClick={(e) => handleRescheduleBooking(booking.id, e)}
              disabled={reschedulingId === booking.id}
            >
              {reschedulingId === booking.id ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Pencil className="h-3 w-3" />
              )}
              Reschedule
            </Button>
          )}

          {canCancel(booking.status as BookingStatus) && (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1 text-red-600 hover:text-red-700"
              onClick={(e) => handleCancelBooking(booking.id, e)}
              disabled={cancelingId === booking.id}
            >
              {cancelingId === booking.id ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Trash className="h-3 w-3" />
              )}
              Cancel Visit
            </Button>
          )}

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={(e) => copyBookingLink(booking.uid, e)}
                >
                  <Copy className="h-3 w-3" />
                  Copy Link
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Copy patient visit link to clipboard</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/* Begin Visit Button with more emphasis */}
          {canStartAppointment(booking.status as BookingStatus) && (
            <Button
              variant="default"
              size="sm"
              className="flex items-center gap-1 bg-blue-600 hover:bg-blue-700"
              onClick={(e) => handleStartAppointment(booking.id, e)}
              disabled={startingAppointmentId === booking.id}
            >
              {startingAppointmentId === booking.id ? (
                <Loader2 className="h-3 w-3 animate-spin mr-1" />
              ) : (
                <Mic className="h-3 w-3 mr-1" />
              )}
              Begin AI-Scribed Visit
            </Button>
          )}

          {/* View past recording Button */}
          {booking.actual_start_time && booking.actual_end_time && (
            <Button
              variant="outline"
              size="sm"
              className="flex items-center gap-1"
              onClick={(e) => {
                e.stopPropagation();
                // This would typically open a modal with past recordings
                navigate(
                  `/dashboard/patients/patient/${booking.patient_id}/visits`
                );
              }}
            >
              <FileText className="h-3 w-3" />
              View Clinical Notes
            </Button>
          )}

          {canJoin(booking.status as BookingStatus) &&
            booking.location_details &&
            (typeof booking.location_details === 'string'
              ? booking.location_details.includes('http')
              : booking.location_details.details?.link) && (
              <Button
                variant="default"
                size="sm"
                className="flex items-center gap-1"
                onClick={(e) => e.stopPropagation()}
                asChild
              >
                <a
                  href={
                    typeof booking.location_details === 'string'
                      ? booking.location_details
                      : (booking.location_details.details?.link as string)
                  }
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Video className="h-3 w-3" />
                  Join Telehealth
                </a>
              </Button>
            )}
        </div>
      </div>
    );
  };

  // Empty state when no bookings are found
  if (!isLoading && filteredBookings.length === 0) {
    return (
      <div className="space-y-4">
        <Tabs
          value={activeCategory}
          onValueChange={(value) =>
            setActiveCategory(value as AppointmentCategory)
          }
        >
          <TabsList className="mb-4">
            <TabsTrigger value="UPCOMING" className="relative">
              Upcoming {renderCount(categoryCounts.UPCOMING)}
            </TabsTrigger>
            <TabsTrigger value="ONGOING" className="relative">
              In Progress {renderCount(categoryCounts.ONGOING)}
            </TabsTrigger>
            <TabsTrigger value="COMPLETED" className="relative">
              Completed {renderCount(categoryCounts.COMPLETED)}
            </TabsTrigger>
            <TabsTrigger value="CANCELLED" className="relative">
              Cancelled {renderCount(categoryCounts.CANCELLED)}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="rounded-md border shadow-md p-6">
          <div className="flex flex-col items-center justify-center text-center py-10">
            <Calendar className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold">No patient visits found</h3>
            <p className="text-muted-foreground mt-2 max-w-md">
              There are no visits in this category. They will appear here once
              scheduled.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Tabs
          value={activeCategory}
          onValueChange={(value) =>
            setActiveCategory(value as AppointmentCategory)
          }
        >
          <TabsList>
            <TabsTrigger value="UPCOMING" className="relative">
              Upcoming {renderCount(categoryCounts.UPCOMING)}
            </TabsTrigger>
            <TabsTrigger value="ONGOING" className="relative">
              In Progress {renderCount(categoryCounts.ONGOING)}
            </TabsTrigger>
            <TabsTrigger value="COMPLETED" className="relative">
              Completed {renderCount(categoryCounts.COMPLETED)}
            </TabsTrigger>
            <TabsTrigger value="CANCELLED" className="relative">
              Cancelled {renderCount(categoryCounts.CANCELLED)}
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <Button
          variant="outline"
          size="sm"
          className="p-2 h-9"
          onClick={handleRefresh}
          disabled={isRefreshing || isLoading}
        >
          {isRefreshing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          <span className="ml-2">Refresh</span>
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-4">
          <Skeleton className="h-28 w-full rounded-md" />
          <Skeleton className="h-28 w-full rounded-md" />
          <Skeleton className="h-28 w-full rounded-md" />
        </div>
      ) : (
        <div className="rounded-md border shadow-md">
          {filteredBookings.map((booking, index) => (
            <div
              key={booking.uid}
              className={`cursor-pointer transition-colors hover:bg-muted/50 p-4 ${
                index !== filteredBookings.length - 1 ? 'border-b' : ''
              }`}
              onClick={() => toggleExpand(booking.uid)}
            >
              {getHeaderForBooking(booking)}

              {/* Details section - shown when expanded */}
              {expandedBookingId === booking.uid &&
                renderExpandedContent(booking)}

              {/* Toggle button */}
              <div className="flex justify-center mt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 px-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleExpand(booking.uid);
                  }}
                >
                  {expandedBookingId === booking.uid ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination controls */}
      {!isLoading && totalPages > 1 && renderPagination()}
    </div>
  );
}
